/**
 * IndexedDBAdapter
 *
 * Creates a generic IndexedDB adapter for a single object store.
 * Returns an object with save / load / delete / clear methods.
 *
 * When using multiple stores in the same DB, pass the same dbVersion and
 * allStoreNames so that onupgradeneeded creates every store (avoids
 * "object store was not found" when the DB was created with only one store).
 *
 * @param {{ dbName: string, storeName: string, dbVersion?: number, allStoreNames?: string[] }} config
 * @returns {{
 *   save: (key: string, value: object) => Promise<void>,
 *   load: (key: string) => Promise<object | null>,
 *   delete: (key: string) => Promise<void>,
 *   clear: () => Promise<void>,
 *   getAllKeys: () => Promise<string[]>,
 * }}
 */
export function IndexedDBAdapter({dbName, storeName, dbVersion = 1, allStoreNames}) {
    const storesToEnsure = Array.isArray(allStoreNames) && allStoreNames.length > 0 ? allStoreNames : [storeName]

    /**
     * Open (or create) the IndexedDB database.
     * @returns {Promise<IDBDatabase>}
     */
    function openDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, dbVersion)

            request.onupgradeneeded = (e) => {
                const db = e.target.result
                for (const name of storesToEnsure) {
                    if (!db.objectStoreNames.contains(name)) {
                        db.createObjectStore(name, {keyPath: 'key'})
                    }
                }
            }

            request.onsuccess = () => resolve(request.result)
            request.onerror = () => reject(request.error)
        })
    }

    return {
        /**
         * Save a record. Uses put() so existing records with the same key are overwritten.
         * @param {string} key
         * @param {object} value
         * @returns {Promise<void>}
         */
        async save(key, value) {
            const db = await openDB()
            const tx = db.transaction(storeName, 'readwrite')
            const store = tx.objectStore(storeName)
            store.put({...value, key})
            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
            })
        },

        /**
         * Load a record by key.
         * @param {string} key
         * @returns {Promise<object | null>}
         */
        async load(key) {
            const db = await openDB()
            const tx = db.transaction(storeName, 'readonly')
            const store = tx.objectStore(storeName)
            const request = store.get(key)
            return new Promise((resolve, reject) => {
                request.onsuccess = () => resolve(request.result ?? null)
                request.onerror = () => reject(request.error)
            })
        },

        /**
         * Delete a record by key.
         * @param {string} key
         * @returns {Promise<void>}
         */
        async delete(key) {
            const db = await openDB()
            const tx = db.transaction(storeName, 'readwrite')
            const store = tx.objectStore(storeName)
            store.delete(key)
            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
            })
        },

        /**
         * Remove all records from the store.
         * @returns {Promise<void>}
         */
        async clear() {
            const db = await openDB()
            const tx = db.transaction(storeName, 'readwrite')
            const store = tx.objectStore(storeName)
            store.clear()
            return new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve()
                tx.onerror = () => reject(tx.error)
            })
        },

        /**
         * Get all keys from the store.
         * @returns {Promise<string[]>}
         */
        async getAllKeys() {
            const db = await openDB()
            const tx = db.transaction(storeName, 'readonly')
            const store = tx.objectStore(storeName)
            const request = store.getAllKeys()
            return new Promise((resolve, reject) => {
                request.onsuccess = () => resolve(request.result ?? [])
                request.onerror = () => reject(request.error)
            })
        },
    }
}
